import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useConferences, usePapers } from '../lib/api';
import type { ConferencePaperDto } from '../types';
import { fmtDate } from './_shared';

// Tab — Papers (conference intelligence). SPE/IADC conference papers via Crossref
// (member 861 — includes the SPE/IADC joint conferences), Haiku-scored 0-100 for
// relevancy to the acquisition thesis. Author affiliations are the small-company
// discovery source; matched ones link to dossiers. Scores are ADVISORY research
// signals — nothing here publishes events.

const SCORE_CHIPS = [0, 40, 60, 80];

export default function Papers() {
  const [minScore, setMinScore] = useState(60);
  const [conference, setConference] = useState('');
  const [page, setPage] = useState(0);
  const { data, isLoading, error } = usePapers(minScore, conference, '', page);
  const { data: conferences } = useConferences();

  if (isLoading) return <LoadingSkeleton lines={10} />;
  if (error || !data) return <ErrorState message={(error as Error | null)?.message} />;

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Conference papers</CardTitle>
        <CardDescription className="mt-2">
          SPE and IADC conference papers (incl. the joint conferences), pulled weekly from
          Crossref and scored 0–100 for thesis relevancy. Author affiliations feed candidate
          discovery — matched companies link to their dossiers. Attendee-gated presentation
          decks are not captured (documented gap); every title links its DOI.
        </CardDescription>
      </Card>

      {/* Upcoming conferences strip */}
      {conferences && conferences.length > 0 && (
        <Card>
          <span className="eyebrow">Upcoming conferences</span>
          <div className="mt-3 flex flex-wrap gap-2">
            {conferences.slice(0, 12).map((c) => (
              <a
                key={c.slug}
                href={c.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-xs text-muted transition-colors hover:text-text font-(family-name:--font-ui)"
                title={c.name}
              >
                <span className="uppercase tracking-[0.1em] text-subtle">{c.organizer}</span>
                <span className="max-w-56 truncate">{c.name}</span>
                {c.startsOn && <span className="[font-variant-numeric:tabular-nums]">{fmtDate(c.startsOn)}</span>}
              </a>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
            Min relevancy
          </span>
          {SCORE_CHIPS.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={minScore === s}
              onClick={() => {
                setMinScore(s);
                setPage(0);
              }}
              className={cn(
                'inline-flex items-center rounded-full border px-3 py-1.5',
                'font-(family-name:--font-ui) text-xs font-medium uppercase tracking-[0.12em] transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                minScore === s
                  ? 'border-accent bg-surface-elevated text-accent'
                  : 'border-border bg-surface text-muted hover:text-text'
              )}
            >
              {s === 0 ? 'All' : `≥ ${s}`}
            </button>
          ))}
          <input
            value={conference}
            onChange={(e) => {
              setConference(e.target.value);
              setPage(0);
            }}
            placeholder="Filter by conference…"
            className="ml-2 w-56 rounded-(--radius-sm) border border-border bg-surface px-2 py-1.5 text-xs text-text placeholder:text-subtle focus-visible:outline-2 focus-visible:outline-accent"
          />
          <span className="ml-auto text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui) [font-variant-numeric:tabular-nums]">
            {data.total} papers
          </span>
        </div>
      </Card>

      <Card>
        {data.items.length === 0 ? (
          <CardDescription>
            No papers match — the weekly mna-conference-poll (Sun 05:22 UTC) fills this, or
            trigger it from Debug.
          </CardDescription>
        ) : (
          <>
            <ol className="flex flex-col">
              {data.items.map((p) => (
                <PaperRow key={p.id} paper={p} />
              ))}
            </ol>
            {pages > 1 && (
              <div className="mt-4 flex items-center gap-3 border-t border-border pt-3">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="text-xs text-accent disabled:text-subtle hover:underline font-(family-name:--font-ui)"
                >
                  ← Newer
                </button>
                <span className="text-xs text-muted [font-variant-numeric:tabular-nums]">
                  page {page + 1} of {pages}
                </span>
                <button
                  type="button"
                  disabled={page + 1 >= pages}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-xs text-accent disabled:text-subtle hover:underline font-(family-name:--font-ui)"
                >
                  Older →
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function scoreTone(score: number): string {
  if (score >= 80) return 'text-score-high';
  if (score >= 60) return 'text-accent';
  if (score >= 40) return 'text-muted';
  return 'text-subtle';
}

function PaperRow({ paper: p }: { paper: ConferencePaperDto }) {
  const affiliations = [...new Set(p.authors.map((a) => a.affiliation).filter(Boolean))] as string[];
  return (
    <li className="flex gap-4 border-b border-border py-3 last:border-0">
      <span
        className={cn(
          'w-12 flex-shrink-0 pt-0.5 text-right font-(family-name:--font-ui) text-lg font-bold [font-variant-numeric:tabular-nums]',
          p.relevancyScore == null ? 'text-subtle' : scoreTone(p.relevancyScore)
        )}
        title={p.relevancyRationale ?? 'not scored yet'}
      >
        {p.relevancyScore == null ? '—' : Math.round(p.relevancyScore)}
      </span>
      <div className="min-w-0 flex-1">
        <a
          href={p.url}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-text hover:text-accent hover:underline"
        >
          {p.title} <ExternalLink className="inline h-3 w-3 align-baseline text-muted" aria-hidden />
        </a>
        <p className="mt-0.5 text-xs text-muted">
          {p.conference ?? 'Conference n/a'} · <span className="[font-variant-numeric:tabular-nums]">{fmtDate(p.publishedDate)}</span>
        </p>
        {p.relevancyRationale && (
          <p className="mt-1 text-xs italic text-subtle">{p.relevancyRationale}</p>
        )}
        {affiliations.length > 0 && (
          <p className="mt-1 flex flex-wrap gap-1.5">
            {affiliations.slice(0, 6).map((a) => (
              <span
                key={a}
                className="rounded-(--radius-sm) bg-surface-elevated px-1.5 py-px text-[0.65rem] text-muted font-(family-name:--font-ui)"
              >
                {a.split(',')[0]}
              </span>
            ))}
            {p.matchedCompanySlugs.map((slug) => (
              <Link
                key={slug}
                to={`/mna/companies/${slug}`}
                className="rounded-(--radius-sm) border border-accent px-1.5 py-px text-[0.65rem] text-accent hover:bg-surface-elevated font-(family-name:--font-ui)"
              >
                {slug}
              </Link>
            ))}
          </p>
        )}
      </div>
    </li>
  );
}
